import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RefreshCw, AlertTriangle, MessageCircle, Phone } from "lucide-react";
import ExportCsvButton from "@/components/ExportCsvButton";

const fmt = (v) => `$${Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

const severity = (days) => {
  if (days >= 30) return "border-rose-300 bg-rose-100 text-rose-900";
  if (days >= 14) return "border-orange-300 bg-orange-100 text-orange-900";
  if (days >= 7) return "border-amber-300 bg-amber-100 text-amber-900";
  return "border-yellow-300 bg-yellow-100 text-yellow-900";
};

export default function AdminArrears() {
  const [data, setData] = useState({ items: [], count: 0, total_overdue: 0, as_of: "" });
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const fetchArrears = () =>
    api.get("/admin/arrears")
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));

  useEffect(() => { fetchArrears(); }, []);

  const reload = () => { setLoading(true); fetchArrears(); };

  const runOverdue = async () => {
    setRunning(true);
    try {
      const { data: res } = await api.post("/admin/notifications/run-overdue");
      toast.success(`Overdue scan done — ${res.sent} reminder(s) sent.`);
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <DashboardLayout role="admin">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-ink sm:text-3xl">Arrears</h1>
          <p className="mt-1 text-sm text-ink-soft">Active loans with installments past their due date — prioritise collections at a glance.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ExportCsvButton endpoint="arrears.csv" filename="jrc_arrears.csv" testId="arrears-export-csv" />
          <Button onClick={reload} variant="outline" className="border-[#5B2C82] text-[#5B2C82]" data-testid="arrears-refresh-btn">
            <RefreshCw className="mr-1.5 h-4 w-4" /> Refresh
          </Button>
          <Button onClick={runOverdue} disabled={running} className="bg-rose-600 text-white hover:bg-rose-700" data-testid="arrears-run-overdue-btn">
            <AlertTriangle className="mr-1.5 h-4 w-4" /> {running ? "Sending…" : "Send overdue reminders"}
          </Button>
        </div>
      </div>

      <div className="mb-5 grid grid-cols-2 gap-4 sm:grid-cols-3">
        <Stat label="Loans in arrears" value={data.count} testid="arrears-count" />
        <Stat label="Total overdue" value={fmt(data.total_overdue)} testid="arrears-total" />
        <Stat label="As of" value={data.as_of || "—"} testid="arrears-asof" />
      </div>

      <div className="overflow-hidden rounded-xl border border-[#E5E2DE] bg-white">
        <div className="hidden grid-cols-12 gap-4 border-b border-[#E5E2DE] bg-[#F9F6F0] px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-muted md:grid">
          <div className="col-span-3">Borrower</div>
          <div className="col-span-2 text-right">Overdue amount</div>
          <div className="col-span-1 text-right">Items</div>
          <div className="col-span-2 text-right">Days overdue</div>
          <div className="col-span-2">Last reminder</div>
          <div className="col-span-2 text-right">Contact</div>
        </div>
        {loading && <div className="px-5 py-10 text-center text-sm text-ink-soft">Loading…</div>}
        {!loading && data.items.length === 0 && (
          <div className="px-5 py-10 text-center text-sm text-ink-soft" data-testid="arrears-empty">🎉 No loans in arrears. Everyone is up to date.</div>
        )}
        {data.items.map((r) => (
          <div key={r.loan_id} data-testid={`arrears-row-${r.ref}`}
            className="grid grid-cols-1 gap-2 border-b border-[#E5E2DE] px-5 py-4 last:border-b-0 md:grid-cols-12 md:items-center md:gap-4">
            <div className="md:col-span-3">
              <div className="font-medium text-ink">{r.applicant_name || "—"}</div>
              <div className="text-xs text-ink-muted">Loan {r.ref} · oldest due {r.oldest_due_date}</div>
            </div>
            <div className="text-right font-semibold text-rose-700 md:col-span-2">{fmt(r.overdue_amount)}</div>
            <div className="text-right text-sm text-ink-soft md:col-span-1">{r.overdue_count}</div>
            <div className="md:col-span-2 md:text-right">
              <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${severity(r.max_days_overdue)}`} data-testid={`arrears-days-${r.ref}`}>
                {r.max_days_overdue} day{r.max_days_overdue === 1 ? "" : "s"}
              </span>
            </div>
            <div className="text-xs text-ink-soft md:col-span-2">
              {r.last_reminder_at ? new Date(r.last_reminder_at).toLocaleDateString() : "Not sent"}
            </div>
            <div className="flex justify-start gap-2 md:col-span-2 md:justify-end">
              {r.applicant_phone && (
                <a href={`https://wa.me/${String(r.applicant_phone).replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-900 hover:bg-emerald-200" data-testid={`arrears-wa-${r.ref}`}>
                  <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                </a>
              )}
              {r.applicant_phone && (
                <a href={`tel:${r.applicant_phone}`} className="inline-flex items-center gap-1 rounded-full bg-[#F0ECE4] px-2.5 py-1 text-xs font-medium text-ink hover:bg-[#E5E0D6]" data-testid={`arrears-call-${r.ref}`}>
                  <Phone className="h-3.5 w-3.5" /> Call
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-xl border border-[#E5E2DE] bg-white p-4" data-testid={testid}>
      <div className="text-xs uppercase tracking-wider text-ink-muted">{label}</div>
      <div className="mt-1 font-heading text-2xl font-bold text-ink">{value}</div>
    </div>
  );
}
